import React from 'react';

import { isDefined, noop, valOrThrow } from '@nfctron/nexus-core';
import { cn, useContextRequired } from '@nfctron/nexus-react';

import { AppShell, Container } from '@mantine/core';
import { ClockIcon } from 'lucide-react';

import { ProductsViewFooterAtom } from '@root/pages/event/[organizer]/[event]/cart/_resources/atoms/ProductsView/ProductsViewFooter.atom';

import { CountdownAlert } from '@root/lib/components/CountdownAlert/CountdownAlert';
import { MultiView } from '@root/lib/components/MultiView/MultiView';
import { APP_I18N_NAMESPACE } from '@root/lib/constants/common';
import { useTranslate } from '@root/lib/hooks/useTranslation';
import { type AtomComponent } from '@root/lib/types/components';

import { EventCartPageContext } from '../components/CartContext';
import { CART_VIEW, useCartStore } from '../stores/cartStore';

import { CartViewFooterAtom } from './CartView/CartViewFooter.atom';
import { CheckoutViewFooterAtom } from './CheckoutView/CheckoutViewFooter.atom';
import { TicketsViewFooterAtom } from './TicketsView/TicketsViewFooter.atom';

/**
 * Event/Cart/Footer atom component
 * @author filipditrich <ditrich@nfctron.com>
 * @constructor
 */
export const FooterAtom: AtomComponent = () => {
	const { t } = useTranslate(APP_I18N_NAMESPACE.MAIN, FooterAtom.displayName);
	const { footerRef } = useContextRequired(EventCartPageContext);
	const cartedTickets = useCartStore((s) => s.cartedTickets);
	const seatingReservation = useCartStore((s) => s.seatingReservation);
	const isMobileReservationAlert = isDefined(seatingReservation) && isDefined(seatingReservation.reservedUntil);

	return (
		<AppShell.Footer
			styles={{
				footer: {
					position: 'fixed',
					bottom: -((footerRef?.current?.clientHeight ?? 150) + 10),
					...(cartedTickets.length > 0 && { position: 'sticky', bottom: 0, left: 0, right: 0 }),
				},
			}}
			classNames={{ footer: cn('transition-all w-screen border-zinc-200 pb-4 md:pb-2 pt-2 left-0 right-0') }}
			withBorder
			ref={footerRef}
		>
			{isMobileReservationAlert && (
				<CountdownAlert
					className="xl:hidden"
					p={4}
					radius={0}
					date={valOrThrow(seatingReservation?.reservedUntil, 'no active reservation')}
					onExpire={noop}
				>
					{(timeLeft) => (
						<React.Fragment>
							<ClockIcon className="size-4" />
							<span className="text-sm leading-[10px]">{t('reservationAlert.text', { timeLeft })}</span>
						</React.Fragment>
					)}
				</CountdownAlert>
			)}
			<Container
				className={cn(
					'max-w-screen-sm xl:max-w-screen-xl',
					'flex h-full items-center justify-between px-3 py-3 sm:px-4 md:px-6',
					isMobileReservationAlert && 'mt-[-5px] pt-0 xl:mt-0 xl:pt-3',
				)}
			>
				{/* tickets view footer */}
				<MultiView.View view={CART_VIEW.TICKETS}>
					<TicketsViewFooterAtom />
				</MultiView.View>
				{/* cart view footer */}
				<MultiView.View view={CART_VIEW.CART}>
					<CartViewFooterAtom />
				</MultiView.View>
				{/* products view footer */}
				<MultiView.View view={CART_VIEW.PRODUCTS}>
					<ProductsViewFooterAtom />
				</MultiView.View>
				{/* checkout view footer */}
				<MultiView.View view={CART_VIEW.CHECKOUT}>
					<CheckoutViewFooterAtom />
				</MultiView.View>
			</Container>
		</AppShell.Footer>
	);
};

/**
 * Event/Cart/Footer atom display name
 * @type {ComponentDisplayName<"atom">>}
 */
FooterAtom.displayName = 'Event/Cart/FooterAtom';
